import {IGetMetadata} from "../types/metadata";
import {useQuery} from "@tanstack/react-query";
import Api, {DEFAULT_ERROR_MESSAGE} from "../api/Api";
import moment from "moment";
import {DT0Format} from "../utils/StaticVariable";
import {GLOBAL_HD_DTU_ID} from "../utils/common";
import {QUERY_KEYS} from "./queryKeys";
import {IPlotVariable, IRosePlotPreviewData} from "../types/analytics";
import {max} from 'lodash'

export const useRosePlotQuery = (
  datasetId: string,
  coordinates: number[],
  pointName: string,
  timeStepInterval: string,
  metadata: Partial<IGetMetadata>,
  timeSeriesData: any,
  isTimeSeriesFinished: boolean,
  orderId: string,
  enabled: boolean
) => {
  const requiresOrderId = datasetId.includes('CMEMS');

  return useQuery({
    queryKey: [
      QUERY_KEYS.plot.roseplot,
      datasetId,
      coordinates,
      pointName,
      timeStepInterval,
      ...(requiresOrderId ? [orderId] : []), 
    ],
    queryFn: async () => {
      if (metadata?.IsOffline || !metadata.TimeDefinition || !metadata.ItemsInfo || !metadata) {
        return { success: false, error: DEFAULT_ERROR_MESSAGE }
      }

      const timeRange = metadata.TimeDefinition.split(';')

      try {
        const defaultShortName = metadata?.ItemsInfo?.find(
          (item: any) => item.ShortName === metadata.DefaultValueShortName
        )?.Name ?? metadata.ItemsInfo[0].Name

        const formattedDefaultShortName = defaultShortName.replace(/ /g, "_")

        let rosePlotDirectionInterval = 1

        if(timeSeriesData && timeSeriesData?.Data !== null){
          const getTimeSeriesShortName = timeSeriesData?.Data?.Items?.map((item: any) => {
            return parseFloat(item[formattedDefaultShortName])
          }).filter((value: any) => !isNaN(value) && value !== null)
          const maxValue = (getTimeSeriesShortName.length > 0 ? max(getTimeSeriesShortName) : 1) as number

          rosePlotDirectionInterval = Math.max(0.01, maxValue / 8);
        }

        const variableList = metadata.ItemsInfo.filter(
          (item) => item.Name !== 'Time' && !item.Name.includes('Water Level')
        )

        const defaultVariableX = variableList.find(
          (v: IPlotVariable) => v.ShortName === metadata.DefaultValueShortName
        )
        const defaultVariableY = variableList.find(
          (v: IPlotVariable) => v.ShortName === metadata.DefaultDirectionShortName
        )
        const variableX = defaultVariableX ?? variableList[0]
        const variableY = defaultVariableY ?? variableList[0]

        const rosePlotPayload = {
            orderId,
            pointName,
            datasetName: datasetId,
            startDate: moment(timeRange[1]).subtract(1, 'year').format(DT0Format),
            endDate: timeRange[1],
            location: coordinates.join(),
            timeStepInterval: datasetId === GLOBAL_HD_DTU_ID ? parseInt(timeStepInterval) : 60,
            directionalDatasetId: datasetId,
            month: 0,
            legendInterval: rosePlotDirectionInterval,
            valueColumnName: variableX.Name,
            directionColumnName: variableY.Name,
            degreeInterval: 12,
          };

          const rosePlotPreviewData = await Api.chart.getRosePlotV2(
            orderId,
            rosePlotPayload.pointName,
            rosePlotPayload.datasetName,
            rosePlotPayload.startDate,
            rosePlotPayload.endDate,
            rosePlotPayload.location,
            rosePlotPayload.timeStepInterval,
            rosePlotPayload.directionalDatasetId,
            rosePlotPayload.month,
            rosePlotPayload.legendInterval,
            rosePlotPayload.valueColumnName,
            rosePlotPayload.directionColumnName,
            rosePlotPayload.degreeInterval
          );

        if (rosePlotPreviewData?.RosePlot === null) {
          return {
            success: false,
            error: DEFAULT_ERROR_MESSAGE,
            direction: rosePlotDirectionInterval
          }
        }

        if (rosePlotPreviewData) {
          return {
            success: true,
            direction: rosePlotDirectionInterval,
            data: {
              RosePlot: rosePlotPreviewData,
              ItemsInfo: metadata.ItemsInfo,
              DefaultDirectionShortName: metadata.DefaultDirectionShortName,
              DefaultValueShortName: metadata.DefaultValueShortName,
            } as IRosePlotPreviewData
          }
        }

        return {
          success: false,
          error: DEFAULT_ERROR_MESSAGE,
          direction: rosePlotDirectionInterval
        }
      } catch (error: any) {
        let errorMessage = DEFAULT_ERROR_MESSAGE

        if (error?.response?.data?.Message) {
          errorMessage = error.response.data.Message
        } else if (error?.message) {
          errorMessage = error.message
        }

        return {
          success: false,
          error: errorMessage
        }
      }
    },
    enabled: !metadata?.IsOffline && isTimeSeriesFinished && !!datasetId && !!coordinates.length && enabled && (requiresOrderId ? !!orderId : true),
  })
}
